<template>
  <div class="z-(--z-nav)">
    <div
      id="tracklify-top-header"
      class="headerWrapper relative z-(--z-nav) flex items-center h-12
        px-4 border-b border-line text-text [-webkit-app-region:drag]
        before:content-[''] before:absolute before:inset-0 before:-z-1 before:pointer-events-none
        before:bg-chrome before:[backdrop-filter:var(--glass-blur)]
        max-sm:h-(--mobile-header-height) max-sm:px-3 max-sm:transition-colors max-sm:before:bg-popover-solid"
      :class="mobileMode && !pageScrolled && 'border-popover-solid'"
    >

      <SimpleButton size="sm" :class="GHOST_BTN" class="sm:hidden max-sm:size-10! max-sm:min-h-10! max-sm:text-text!" v-on="headerProps.listeners">
        <IconArrowLeftOutline :class="GHOST_ICON" />
      </SimpleButton>
      <div v-if="inApp" class="flex items-center gap-px mr-1.5 [-webkit-app-region:no-drag] max-sm:hidden">
        <SimpleButton size="sm" :class="GHOST_BTN" @click="router.back()">
          <IconArrowLeftOutline :class="GHOST_ICON" />
        </SimpleButton>
        <SimpleButton size="sm" :class="GHOST_BTN" @click="appReload">
          <span class='tr tr-reload' :class="GHOST_ICON"></span>
        </SimpleButton>
      </div>
      <div
        class="flex items-center text-base font-bold tracking-[-0.01em] text-[var(--ink)]
          max-sm:absolute max-sm:left-1/2 max-sm:max-w-[calc(100%-13rem)] max-sm:-translate-x-1/2
          max-sm:text-lg"
      >
        <div
          v-if="headerProps.title"
          class="truncate max-sm:max-w-full"
          :class="[headerProps.titleClass, TITLE_WIDTH[headerProps.titleClass]?.[updateAvailable ? 1 : 0]]"
        >
          {{ headerProps.title }}
        </div>
        <SkeletonLoader
          v-else-if="project"
          type="text"
        />
      </div>
      <div
        class="contents"
        :class="filterInHeader && 'max-sm:ml-auto max-sm:flex max-sm:items-center'"
      >
      <div
        v-if="showSearcher"
        class="search-slot absolute inset-y-0 left-[calc(50%-13.75rem)] w-105 flex items-center
          [-webkit-app-region:no-drag]
          max-xl:static max-xl:w-auto max-xl:ml-auto"
        @mousedown="e => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }"
      >
        <Searcher :disabled="loadingProject" />
      </div>
      <SimpleButton
        v-if="filterInHeader"
        size="sm"
        class="size-10! min-h-10! rounded-full! border-0! bg-transparent! px-0!"
        :class="activeFilterCount ? 'text-accent!' : 'text-text!'"
        @click="toggleFilters"
      >
        <IconFilter2 class="size-5" />
      </SimpleButton>
      </div>

      <div
        class="ml-auto flex items-center gap-1.5 [-webkit-app-region:no-drag]"
        :class="showSearcher && 'max-sm:ml-1.5'"
      >
        <div id="header-actions" class="contents"></div>
        <SimpleButton
          v-if="mobileMode && showNotifications && unhandledNotifics"
          size="sm"
          class="h-10! min-h-10! gap-1.5! rounded-full! border-0! bg-transparent! px-2! whitespace-nowrap text-label font-medium text-text!"
          :title="t('notifications.mark_all_read')"
          @click="store.dispatch('user/actionMarkAllNotificationsRead')"
        >
          <IconChecks class="size-5" />
          {{ t('notifications.read_all') }}
        </SimpleButton>
        <div
          v-if="UPDATE_TONE[updateAvailable]"
          class="flex items-center gap-1.5 h-7 px-[0.6875rem] rounded-chip border text-[0.75rem] font-bold
            cursor-pointer transition-[background-color,color] duration-150
            max-sm:p-[0.4375rem]"
          :class="[
            UPDATE_TONE[updateAvailable],
            activeUpdating && 'cursor-default pointer-events-none',
          ]"
          @click="installUpdate"
        >
          <i
            class="tr tr-update-arrow flex items-center h-4"
            :class="activeUpdating && 'animate-spin [animation-duration:2s]'"
          ></i>
          <span class="max-sm:hidden">{{ updateLabel }}</span>
          <span
            v-if="updateProgress !== null && updateProgress < 100"
            class="w-[4ch] text-right tabular-nums max-sm:hidden"
          >{{ updateProgress }}%</span>
        </div>

        <NewYearLetterButton />

        <Teleport defer to="#mobile-nav-account" :disabled="!mobileMode">
        <div class="fixed inset-0 z-0" v-if="showNotifications"
          @click="setShowNotifications(false)"
        ></div>
        <div class="relative flex items-center justify-center cursor-pointer max-sm:w-[20vw]" :title="unhandledNotificsAsWords">
          <SimpleButton
            size="sm"
            class="relative w-8 px-0!
                   max-sm:h-16! max-sm:w-full! max-sm:min-h-0! max-sm:flex-col max-sm:gap-1
                   max-sm:border-0! max-sm:bg-transparent!"
            :class="showNotifications && ACTIVE_BTN"
            @click.stop="toggleNotifications"
          >
            <IconBell class="size-[1.125rem] max-sm:size-6" />
            <span class="mobile-nav-label">{{ t('menu.nav_alerts') }}</span>
          </SimpleButton>
          <span
            v-if="unhandledNotifics"
            class="pointer-events-none absolute -top-1.5 -right-1.5 z-1 max-sm:top-2 max-sm:right-[calc(50%-1.25rem)]
              h-4 min-w-4 px-1 rounded-full bg-red text-center text-white
              text-[0.625rem] font-bold leading-4 tabular-nums"
          >{{ unhandledNotifics }}</span>
          <Teleport to="body">
            <div
              class="fixed bottom-0 z-(--z-modal) w-[22.5rem] pt-[1.5625rem] pb-[0.9375rem] pl-5
                bg-chrome [backdrop-filter:var(--glass-blur)] shadow-[1px_1px_6px_#00000034]
                transition-all duration-150
                max-sm:top-(--mobile-header-height) max-sm:bottom-(--mobile-nav-height) max-sm:w-full
                max-sm:bg-bg max-sm:px-4 max-sm:pt-5"
              :class="[
                showBoardBar ? 'top-23' : 'top-12',
                showNotifications ? 'right-0' : '-right-[22.5rem] max-sm:-right-full',
              ]"
              @click.stop
            >
              <NotificationWrapper
                v-if="showNotifications"
                @close="setShowNotifications(false)"
              />
            </div>
          </Teleport>
        </div>

        <template v-if="mobileMode && user">
          <div class="flex h-16 w-[20vw] cursor-pointer flex-col items-center justify-center gap-1" @click="userSheetOpen = !userSheetOpen">
            <UserAvatar withDayoffBadge :user='{...getUser(user.id), registrationCompleted: true}' />
            <span class="mobile-nav-label" :class="userSheetOpen ? 'text-accent' : 'text-text'">{{ t('menu.nav_me') }}</span>
          </div>
          <BottomSheet v-model="userSheetOpen" @closed="onUserSheetClosed">
            <div class="flex items-center gap-3 p-3">
              <UserAvatar :user='{...getUser(user.id), registrationCompleted: true}' />
              <span class="min-w-0 flex-1 truncate text-body font-bold text-text">{{ user.username }}</span>
              <SimpleButton variant="ghost" size="sm" class="text-red!" @click="onUserItemClick({ key: 'logout' })">
                {{ t('menu.logout') }}
              </SimpleButton>
            </div>
            <template v-for="group in userMenuGroups" :key="group[0].key">
              <div class="mx-3 my-1 h-px bg-line"></div>
              <div
                v-for="item in group"
                :key="item.key"
                class="flex cursor-pointer items-center gap-3 rounded-control p-3 text-body text-text active:bg-hover"
                @click="onUserItemClick(item)"
              >
                <component :is="USER_MENU_ICONS[item.key]" class="size-5 shrink-0 text-text-muted" />
                <span class="flex-1">{{ t(item.value) }}</span>
                <span v-if="item.suffix" class="text-unit text-text-muted">{{ item.suffix }}</span>
                <ToggleSwitch v-if="item.key === 'darkmode'" class="pointer-events-none flex-none!" :modelValue="!isLight" />
              </div>
            </template>
          </BottomSheet>
        </template>
        <Dropdown
          v-else
          class="user-menu [-webkit-app-region:no-drag]"
          ref="userMenu"
          stickRight
        >
          <template #wrapper>
            <div class="flex items-center cursor-pointer" v-if='user'>
              <UserAvatar withDayoffBadge :user='{...getUser(user.id), registrationCompleted: true}' />
            </div>
          </template>
          <template #items>
            <div class="flex items-center gap-2.5 px-2 pt-1 pb-3">
              <UserAvatar :user='{...getUser(user.id), registrationCompleted: true}' />
              <div class="min-w-0">
                <div class="truncate text-body font-bold text-text">{{ user.username }}</div>
                <div class="truncate text-label text-text-muted">{{ user.email }}</div>
              </div>
            </div>
            <template v-for="group in userMenuGroups" :key="group[0].key">
              <div class="-mx-2 my-1 h-px bg-line"></div>
              <div
                v-for="item in group"
                :key="item.key"
                class="group/item flex h-8.5 cursor-pointer items-center gap-2.5 rounded-chip px-2 text-body text-text
                       transition-colors duration-100 hover:bg-hover hover:text-accent"
                @click="onUserItemClick(item)"
              >
                <component
                  v-if="USER_MENU_ICONS[item.key]"
                  :is="USER_MENU_ICONS[item.key]"
                  class="size-4.5 shrink-0 text-text-muted transition-colors duration-100 group-hover/item:text-accent"
                />
                <span class="flex-1 truncate">{{ t(item.value) }}</span>
                <span v-if="item.suffix" class="text-unit text-text-muted">{{ item.suffix }}</span>
                <ToggleSwitch v-if="item.key === 'darkmode'" class="pointer-events-none flex-none!" :modelValue="!isLight" />
              </div>
            </template>
            <div class="-mx-2 my-1 h-px bg-line"></div>
            <div
              class="flex h-8.5 cursor-pointer items-center gap-2.5 rounded-chip px-2 text-body text-red
                     transition-colors duration-100 hover:bg-red-soft"
              @click="onUserItemClick({ key: 'logout' })"
            >
              <IconArrowRightStartOnRectangle class="size-4.5 shrink-0" />
              {{ t('menu.logout') }}
            </div>
          </template>
        </Dropdown>
        </Teleport>

        <div v-if="inApp" class="w-px h-[1.375rem] mx-[0.1875rem] bg-line"></div>
        <WindowControls v-if="inApp" />
      </div>
    </div>

    <div
      v-if="showBoardBar"
      class="board-bar relative flex items-center gap-3.5 h-11 px-4 border-b border-line
        [-webkit-app-region:no-drag]
        before:content-[''] before:absolute before:inset-0 before:-z-1 before:pointer-events-none
        before:bg-chrome before:[backdrop-filter:var(--glass-blur)]"
    >
      <span
        class="text-muted font-bold tracking-[0.06em] uppercase text-[var(--ink)] whitespace-nowrap light:text-accent"
      >{{ t('taskFilters.assignee') }}</span>
      <div class="flex items-center [-webkit-app-region:no-drag] max-sm:hidden">
        <div class="flex items-center" v-if='project && user && sortedProjectmembers.activeMembers.length'>
          <div class="images flex items-center" v-show="'project' === route.name">
            <div class="wraper-images flex items-center -space-x-1.25 rounded-[1.25rem] pl-1.25 font-bold leading-none">
              <UserAvatar withDayoffBadge
                size="sm"
                v-show='getUser(pm.userId)'
                :key='`pm-${pm.id}`'
                v-for='pm in sortedProjectmembers.activeMembers'
                :user='getUser(pm.userId)'
                :active="userIsActive(pm.userId)"
                :selected="isUserSelectedForTasksOn(pm.userId)"
                @click='onMemberSelect(getUser(pm.userId))'
              />
              <UserAvatar
                v-if='!sortedProjectmembers.hideMembers.length'
                size="sm"
                :user='null'
                :selected="isUserSelectedForTasksOn(null)"
                @click='onMemberSelect({ id: null })'
              />
              <div v-if='sortedProjectmembers.hideMembers.length' class="ml-1.25">
                <Dropdown
                  ref='userlist'
                  class="pm-list-dropdown"
                >
                  <template v-slot:wrapper>
                    <div class="wraper-images flex items-center rounded-[1.25rem] font-bold leading-none">
                      <span
                        class="flex items-center justify-center size-7 rounded-[1.5625rem]
                          bg-bg2 text-[0.75rem]"
                        :class="isHiddenMemberSelected && 'shadow-[0_0_0_1px_var(--bg),0_0_0_2px_var(--text-muted)]'"
                      >
                        +{{sortedProjectmembers.hideMembers.length}}
                      </span>
                    </div>
                  </template>
                  <template v-slot:items>
                    <i18n-t keypath="ui.header_hidden_members" tag="div" class='text-center px-[0.9375rem] py-2 mb-2.5 font-normal text-text'>
                      <template #count><strong>{{sortedProjectmembers.hideMembers.length}}</strong></template>
                    </i18n-t>
                    <AppSearchBox
                      class="w-64"
                      :items="hiddenMemberSelectItems"
                      :getSearchText="u => userSearchText(u, t('search.unassigned'))"
                      :placeholder="t('reports.search')"
                      @select="item => onMemberSelect(item || { id: null })"
                    >
                      <template #item="{ item }">
                        <UserAvatar withDayoffBadge
                          :user="item"
                          :active="item && userIsActive(item.id)"
                          :selected="isUserSelectedForTasksOn(item ? item.id : null)"
                        />
                        <div class="truncate text-left">
                          {{ item ? item.username : t('search.unassigned') }}
                        </div>
                      </template>
                    </AppSearchBox>
                  </template>
                </Dropdown>
              </div>
            </div>
            <Dropdown
              class="invite-dropdown ml-2.25 z-(--z-nav)"
              ref='userlist'
              @visibilityChanged="inviteOpened=$event">

              <template v-slot:wrapper>
                <SimpleButton
                  size="sm"
                  class="!text-muted hover:!text-accent justify-center border-dashed"
                  :class="ICON_BTN_28"
                  :title='t("search.invite_user")'
                >
                  <IconPlus class="size-3.5 shrink-0 stroke-[3]" />
                </SimpleButton>
              </template>
              <template v-slot:items>
                <InviteToProject
                  :key='`itp-${inviteOpened}`'
                  @infoAfterInvite="(e) => { emailAfterInvite = e }"
                  @close='() => userlist.close()'
                />
              </template>
            </Dropdown>
          </div>
        </div>
        <div class="flex" v-else v-show="'project' === route.name">
          <SkeletonLoader type="avatar"/>
          <SkeletonLoader class="-ml-1.25 max-sm:mr-2.5" type="avatar"/>
        </div>
      </div>
      <div v-if="activeFilterCount" class="w-px h-5.5 shrink-0 bg-line"></div>
      <div class="flex min-w-0 flex-1 items-center justify-start overflow-hidden">
        <FilterChips />
      </div>
      <div
        v-if="route.name === 'project' && project && project.id && user"
        class="flex items-center [-webkit-app-region:no-drag] max-sm:mx-0.5"
      >
        <TagFocusButton />
      </div>
      <SimpleButton
        size="sm"
        class="min-w-21"
        :class="activeFilterCount && ACTIVE_BTN"
        @click="toggleFilters"
      >
        <svg :class="HEADER_ICON" viewBox="0 0 15 15" aria-hidden="true">
          <g stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
            <line x1="2" y1="4" x2="13" y2="4" />
            <line x1="4" y1="7.5" x2="11" y2="7.5" />
            <line x1="6" y1="11" x2="9" y2="11" />
          </g>
        </svg>
        <span>{{ t('taskFilters.filter') }}</span>
        <span v-if="activeFilterCount">{{ activeFilterCount }}</span>
      </SimpleButton>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { setDocumentTitle } from '@/utils/documentTitle';
import { userSearchText } from '@/utils';

import Searcher from '@/components/widgets/Searcher';
import FilterChips from '@/components/widgets/FilterChips';
import { useTaskFilterChips } from '@/composables/useTaskFilterChips';
import eventBus from '@/eventBus';
import InviteToProject from '@/components/widgets/InviteToProject';
import SkeletonLoader from '@/components/elements/SkeletonLoader';
import UserAvatar from '@/components/elements/UserAvatar';
import NewYearLetterButton from '@/components/newYear/NewYearLetterButton.vue';
import AppSearchBox from '@/components/elements/AppSearchBox';
import Dropdown from '@/components/elements/Dropdown';
import TagFocusButton from '@/components/elements/TagFocusButton.vue';
import ToggleSwitch from '@/components/elements/ToggleSwitch';
import { isLight } from '@/ui/tokens/themes';
import config from '@/config';
import NotificationWrapper from '@/components/elements/NotificationWrapper';
import BottomSheet from '@/components/elements/BottomSheet.vue';
import WindowControls from '@/components/elements/WindowControls.vue';
import {
  IconArrowDownTray, IconBell, IconInformationCircle, IconMoon, IconPhoto, IconPlus,
  IconQuestionMarkCircle, IconSparkles, IconUser, IconArrowRightStartOnRectangle,
} from '@iconify-prerendered/vue-heroicons';
import { IconChecks, IconFilter2 } from '@iconify-prerendered/vue-tabler';
import { IconArrowLeftOutline } from '@iconify-prerendered/vue-flowbite';

// Tailwind sets shared by the header buttons. `!` beats the matching utility
// baked into SimpleButton's size / variant.
const HEADER_ICON = 'flex flex-none items-center justify-center size-4 text-sm';
const ICON_BTN_28 = 'size-7! min-h-7! px-0!';
const GHOST_BTN = `${ICON_BTN_28} border-transparent! bg-transparent! text-text-muted!
  enabled:hover:border-transparent! enabled:hover:bg-hover! enabled:hover:text-accent!`;
const GHOST_ICON = 'flex flex-none items-center justify-center size-5 text-[1.125rem]';
const ACTIVE_BTN = 'bg-accent-soft! border-accent! text-accent!';

// update chip tone, keyed by store `updateAvailable` ('' while up to date)
const UPDATE_TONE = {
  critical: `bg-red-soft border-[color-mix(in_srgb,var(--red)_40%,transparent)] text-red
    hover:bg-red hover:text-[var(--on-red)]`,
  minor: `bg-[color-mix(in_srgb,var(--orange)_16%,transparent)]
    border-[color-mix(in_srgb,var(--orange)_40%,transparent)] text-orange
    hover:bg-orange hover:text-[var(--on-orange)]`,
  patch: `bg-[color-mix(in_srgb,var(--green)_16%,transparent)]
    border-[color-mix(in_srgb,var(--green)_40%,transparent)] text-green
    hover:bg-green hover:text-[var(--on-green)]`,
};

// title width per kind: [normal, narrowed so the update chip fits]
const TITLE_WIDTH = {
  'project-name': [
    'sm:max-[1200px]:max-w-[9.375rem]',
    'sm:max-[1200px]:max-w-[7.8125rem]',
  ],
  'task-name': [
    'sm:max-[1200px]:max-w-[37.5rem]',
    'sm:max-[1200px]:max-w-[34.375rem]',
  ],
};

// Reactive state
const hideForDrag = ref(false);
const activeUpdating = ref(false);
const updateProgress = ref(null);
const updateFailureAlertId = 'app-update-failed';
const emailAfterInvite = ref('');
const inviteOpened = ref(false);
const appVersion = ref('');
const showNotifications = ref(false);
const userSheetOpen = ref(false);

// Template refs
const userlist = ref(null);
const userMenu = ref(null);
const windowTitlesSetupNeeded = ref(false); // GNOME Wayland without the window titles extension

// Composables
const store = useStore();
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { activeCount: activeFilterCount } = useTaskFilterChips();

// Props (if any - this component doesn't seem to have props)
const props = defineProps({
  mainHeader: Boolean,
});

// Computed properties (Vuex state)
const project = computed(() => store.state.project.project);
const taskUnderEdit = computed(() => store.state.project.taskUnderEdit);
const user = computed(() => store.state.user.user);
const workspace = computed(() => store.state.user.workspace);
const updateAvailable = computed(() => store.state.user.updateAvailable);
const updateLabel = computed(() => (updateProgress.value === 100
  ? t('trackingNotice.__installing_update')
  : t('trackingNotice.__update_tracker')));
const unhandledNotifics = computed(() => store.state.user.unhandledNotifics);
const mobileMode = computed(() => store.state.system.mobileMode);
const openedDialog = computed(() => store.state.system.openedDialog);
const pageScrolled = computed(() => store.state.system.pageScrolled);

// Computed properties (Vuex getters)
const getUser = computed(() => store.getters['user/getUser']);
const isUserSelectedForTasksOn = computed(() => store.getters['project/isUserSelectedForTasksOn']);
const sortedProjectmembers = computed(() => store.getters['project/sortedProjectmembers']);
const hiddenMemberSelectItems = computed(() => [
  null,
  ...sortedProjectmembers.value.hideMembers
    .map(pm => getUser.value(pm.userId))
    .filter(Boolean),
]);
const isHiddenMemberSelected = computed(() => hiddenMemberSelectItems.value
  .some(item => isUserSelectedForTasksOn.value(item ? item.id : null)));
// Other computed properties
const pageHeader = computed(() => {
  if (mobileMode.value) {
    if (openedDialog.value === 'NotificationWrapper') {
      return t('menu.notifications');
    } if (openedDialog.value === 'CreateTask') {
      return t('menu.task_create');
    }
  }
  const routeName = route.matched[route.matched.length - 1]?.name;
  const metaTitle = route.matched[route.matched.length - 1]?.meta?.title;
  if (metaTitle) {
    // Prefer route meta title when provided; treat string as i18n key
    return typeof metaTitle === 'string' ? t(metaTitle) : metaTitle;
  }
  return {
    projectSettings: t('menu.project_settings'),
    myTasks: t('menu.myTasks'),
    'tasks-time-spent': t('menu.time_report'),
    'invoice-generator': t('menu.invoice_generator'),
    'workspace-summary': t('menu.summary_report'),
    salary: t('menu.salary'),
    'profit-share': t('menu.profit_share'),
    createProject: t('menu.create_project'),
    runApp: t('appRunner.authorize_app'),
    workspaceSettings: t('menu.workspace_settings'),
    profileSettings: t('profile.Configure_user_profile'),
    getApp: t('appRunner.download_app'),
    aiAutomation: t('menu.ai_automation'),
  }[routeName] || '';
});

const taskShortDescription = computed(() => {
  let result = taskUnderEdit.value?.textMD || '';
  if (result) {
    if (result.length > 88) {
      result = `${result.substring(0, 88)}...`;
    }
  } else {
    result = t('task.noDescription');
  }
  return result;
});

const inApp = computed(() => {
  return window.APP_TRON;
});

const userIsActive = computed(() => {
  return userId => project.value.activeTrackUserIds
    && project.value.activeTrackUserIds.some(id => id === userId);
});

const isGuest = computed(() => {
  return workspace.value && !!workspace.value.users
    .find(u => u.id === user.value.id && u.role === 'GUEST');
});

const loadingProject = computed(() => {
  return !(route.name !== 'project' || (project.value && project.value.name));
});

const showBoardBar = computed(() => {
  return route.name === 'project' && !mobileMode.value;
});

const showSearcher = computed(() => {
  return !(hideForDrag.value || taskUnderEdit.value
  || ((mobileMode.value)
  && ['NotificationWrapper', 'CreateTask'].includes(openedDialog.value)));
});

const filterInHeader = computed(() => mobileMode.value && route.name === 'project' && showSearcher.value);

const unhandledNotificsAsWords = computed(() => {
  if (unhandledNotifics.value) {
    return `${unhandledNotifics.value} ` + (unhandledNotifics.value === 1 ? t('menu.new_notification') : t('menu.new_notifications'));
  }
  return t('menu.no_new_notifications');
});

const userMenuItems = computed(() => {
  const items = [
    // hidden for now; uncomment to bring the AI assistant back
    // {
    //   key: 'aiAssistant',
    //   value: 'menu.ai_assistant',
    // },
    {
      key: 'profile',
      value: 'menu.profile',
      route: { name: 'profileSettings' },
    },
    {
      key: 'myScreenshots',
      value: 'menu.my_screenshots',
    },
    {
      key: 'darkmode',
      value: 'menu.dark_mode',
    },
    ...(!window.APP_TRON ? [
      {
        key: 'downloadApp',
        route: { name: 'getApp' },
        value: 'menu.download_app',
      },
    ] : []),
    {
      key: 'aiAutomation',
      route: { name: 'aiAutomation' },
      value: 'menu.ai_automation',
    },
    {
      key: 'about',
      value: 'menu.about',
      suffix:
        ((workspace.value && workspace.value.id === '06854420-e2f6-11e9-83ec-49b05d749750')
          || process.env.VUE_APP_ENV !== 'live')
        && window.APP_TRON
          ? store.state.appVersion
          : false,
    },
    {
      key: 'gethelp',
      value: 'menu.get_help',
    },
    {
      key: 'logout',
      value: 'menu.logout',
    },
    ...(window.APP_TRON ? [
      {
        key: 'openlogs',
        onclick: () => openLogs(),
        value: 'menu.__open_logs',
      },
      {
        key: 'checkupdates',
        onclick: () => checkUpdates(),
        value: 'menu.__check_updates',
      },
      ...(windowTitlesSetupNeeded.value ? [{
        key: 'windowtitles',
        onclick: () => window.APP_TRON.ipcRenderer.send('retryWindowTitlesSetup'),
        value: 'menu.__enable_window_titles',
      }] : []),
      ...((workspace.value && workspace.value.id === '06854420-e2f6-11e9-83ec-49b05d749750') || process.env.VUE_APP_ENV !== 'live' ? [{
        key: 'devtools',
        onclick: () => openDevTools(),
        value: 'menu.__devtools',
      }] : []),
      {
        onclick: () => quit(),
        key: 'quit',
        value: 'menu.__quit',
      },
    ] : []),
  ];
  return items;
});

const headerProps = computed(() => {
  const headerProps = {
    listeners: {
      click: () => router.back(),
    },
    title: '',
    titleClass: '',
  };

  if (mobileMode.value) {
    if (['NotificationWrapper', 'CreateTask'].includes(openedDialog.value)) {
      // for those components click outside is enough, no btn click handler needed
      headerProps.listeners = {};
    }
  }

  if (mobileMode.value && openedDialog.value === 'NotificationWrapper') {
    headerProps.title = t('notifications.title');
    headerProps.titleClass = 'base-name';
  } else if (pageHeader.value) {
    headerProps.title = pageHeader.value;
    headerProps.titleClass = 'base-name';
  } else if (taskUnderEdit.value) {
    headerProps.title = taskShortDescription.value;
    headerProps.titleClass = 'task-name';
  } else if (project.value && project.value.name) {
    headerProps.title = project.value.name;
    headerProps.titleClass = 'project-name';
  }
  return headerProps;
});

const tabTitle = computed(() => {
  if (route.name === 'projectIntegrations') {
    return t('projectSettings.integrations');
  }
  if (pageHeader.value) {
    return pageHeader.value;
  }
  if (taskUnderEdit.value) {
    const text = taskUnderEdit.value.textMD || '';
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  }
  if (project.value && project.value.name) {
    return project.value.name;
  }
  return '';
});

watch(tabTitle, (title) => {
  if (title) {
    setDocumentTitle(title);
  }
}, { immediate: true });
// Lifecycle hooks
onMounted(async () => {
  eventBus.$on('close-user-menu', closeUserMenu);
  eventBus.$on('tasks-filters-opened', closeNotificationsForFilters);

  if (props.mainHeader) {
    eventBus.$on('open-task-dialog', (flag) => {
      hideForDrag.value = flag;
    });
  }

  if (window.APP_TRON) {
    eventBus.on('appUpdateProgress', onUpdateProgress);
    eventBus.on('failedInstallUpdate', onUpdateFailure);
    window.APP_TRON.ipcRenderer.on('windowTitlesSetupNeeded', (_, needed) => {
      windowTitlesSetupNeeded.value = needed;
    });
    appVersion.value = window.APP_TRON.appVersion;
  }
});

onBeforeUnmount(() => {
  eventBus.off('appUpdateProgress', onUpdateProgress);
  eventBus.off('failedInstallUpdate', onUpdateFailure);
  eventBus.$off('close-user-menu', closeUserMenu);
  eventBus.$off('tasks-filters-opened', closeNotificationsForFilters);
  if (props.mainHeader) {
    eventBus.$off('open-task-dialog');
  }
});

// Methods
const onUpdateProgress = (percent) => {
  updateProgress.value = percent;
};

const onUpdateFailure = () => {
  activeUpdating.value = false;
  updateProgress.value = null;
  store.commit('system/showAlert', {
    alertId: updateFailureAlertId,
    message: t('trackingNotice.__update_failed'),
    type: 'warning',
    closable: true,
  });
};

const openDevTools = () => {
  window.APP_TRON.ipcRenderer.send('openDevTools');
};

const openLogs = () => {
  window.APP_TRON.ipcRenderer.send('openLogs');
};

const quit = () => {
  window.APP_TRON.ipcRenderer.send('appQuit');
};

const checkUpdates = async () => {
  const newVersionAvailable = await store.dispatch('user/actionGetAppVersions');
  if (!newVersionAvailable) {
    store.commit('system/showAlert', {
      message: t('notifications.no_new_version_found'),
      type: 'info',
      alertId: 'nnverfound',
      timeout: 3,
      closable: true,
    });
  }
};

const USER_MENU_GROUPS = [
  ['profile', 'myScreenshots'],
  ['darkmode', 'downloadApp', 'aiAutomation'],
  ['about', 'gethelp'],
];
const USER_MENU_ICONS = {
  profile: IconUser,
  myScreenshots: IconPhoto,
  darkmode: IconMoon,
  downloadApp: IconArrowDownTray,
  aiAutomation: IconSparkles,
  about: IconInformationCircle,
  gethelp: IconQuestionMarkCircle,
};
// the desktop app's own items (logs, updates, quit …) go in a last group of their own
const userMenuGroups = computed(() => {
  const grouped = [...USER_MENU_GROUPS.flat(), 'logout'];
  const appItems = userMenuItems.value.filter(item => !grouped.includes(item.key));
  return [
    ...USER_MENU_GROUPS.map(keys => userMenuItems.value.filter(item => keys.includes(item.key))),
    appItems,
  ].filter(group => group.length);
});

watch(userSheetOpen, (open) => {
  if (!open) return;
  setShowNotifications(false);
  store.commit('system/setOpenedDialog', 'UserSheet');
});
watch(openedDialog, (dialog) => {
  if (dialog !== 'UserSheet') userSheetOpen.value = false;
});
const onUserSheetClosed = () => {
  if (openedDialog.value === 'UserSheet') store.commit('system/setOpenedDialog', null);
};

const onUserItemClick = (item) => {
  if (item.key === 'darkmode') {
    store.dispatch('uiTheme/actionToggleMode');
    return;
  }
  closeUserMenu();
  if (item.onclick) {
    item.onclick();
    return;
  }
  if (item.route) {
    router.push(item.route);
    return;
  }
  onUserMenuClick(item.key);
};

const onUserMenuClick = async (key) => {
  const localePath = locale.value !== 'en' ? `/${locale.value}` : '';
  if (key === 'logout') {
    await store.dispatch('user/actionLogout');
    window.location.href = `${config.getSSRDomain()}/login/`;
  } else if (key === 'gethelp') {
    window.open(`https://tracklify.com${localePath}/contact/`, '_blank');
  } else if (key === 'about') {
    window.open(`https://tracklify.com${localePath}/`, '_blank');
  } else if (key === 'myScreenshots') {
    router.push({ name: 'tasks-time-spent', query: { mode: 'screenshots', executors: user.value.id } });
  } else if (key === 'aiAssistant') {
    eventBus.$emit('open-ai-chat');
  }
};

const setShowNotifications = (state) => {
  if (!loadingProject.value) {
    showNotifications.value = state;
    store.commit('system/setOpenedDialog', state ? 'NotificationWrapper' : null);
    if (state) eventBus.$emit('close-tasks-filters');
  }
};

const closeNotificationsForFilters = () => {
  if (showNotifications.value) setShowNotifications(false);
};

const toggleNotifications = () => {
  setShowNotifications(!showNotifications.value);
};

const closeUserMenu = () => {
  userSheetOpen.value = false;
  userMenu.value?.close?.();
};

const toggleFilters = () => {
  eventBus.$emit('toggle-tasks-filters');
};

const appReload = () => {
  window.APP_TRON.ipcRenderer.send('update-application');
};

const onMemberSelect = (user) => {
  eventBus.$emit('memberSelect', user);
};

const installUpdate = () => {
  activeUpdating.value = true;
  updateProgress.value = null;
  store.commit('system/clearAlert', { alertId: updateFailureAlertId });
  store.dispatch('user/actionUpdateApplication');
};

</script>
